/** @jsxRuntime classic */
/** @jsx jsx */
import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { jsx, css } from '@emotion/core';
import LinearProgress from "../progress/linear";
import ButtonIcon from "../button/icon";
import Icon from "../../icons";
import { hexToRGBA } from "../../styles/color-helper";
import { animations } from "../../styles/animation";
import { classNames, useDelayUnmount } from "../../utils";
import { itemsCenter, border, boxBorder, borderRadius4px, breakWord, displayBlock, flexRow, justifyStart, positionRelative, userSelectNone } from "../../styles/general";
import { useTheme, useColor as colors } from "../../theme";
const {
  colors: {
    system: {
      white: systemWhite
    }
  },
  typography: {
    heading3,
    heading4,
    heading5,
    paragraph1,
    paragraph2,
    paragraph3
  },
  spacing,
  zIndex: zIndexCORE
} = useTheme();
const iconMap = new Map([['info', 'Info'], ['success', 'ApprovalFilled'], ['warning', 'Warning'], ['danger', 'CancelFilled']]);
const maxWidthMap = new Map([['tiny', 320], ['small', 400], ['medium', 480]]);
const primaryTypoMap = new Map([['tiny', heading5], ['small', heading4], ['medium', heading3]]);
const iconSizeMap = new Map([['tiny', 16], ['small', 20], ['medium', 24]]);
const linearProgressSizeMap = new Map([['tiny', 2], ['small', 3], ['medium', 4]]);
const transitionTime = 300;
const Alert = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  autoDisappear,
  children,
  className,
  clearAble,
  color,
  duration,
  onClose,
  progressing,
  secondary,
  size,
  text,
  type
}, reference) => {
  type = type || color;
  const ref = useRef(null);
  const clearIconRef = useRef(null);
  const [percentState, setPercentState] = useState(100);
  const [isClose, setIsClose] = useState(false);
  const showAlert = useDelayUnmount(!isClose, transitionTime);
  const mainColor = colors[type];
  const opacityColor = hexToRGBA(mainColor, 0.15);
  const iconName = iconMap.get(type);
  const iconSize = iconSizeMap.get(size);
  const _AlertContainerCSS = AlertContainerCSS(size);
  const _AlertInfoCSS = AlertInfoCSS(mainColor, opacityColor);
  const _AlertTextCSS = AlertTextCSS(clearAble);
  const _AlertPrimaryCSS = AlertPrimaryCSS(secondary, size);
  const _AlertSecondaryCSS = AlertSecondaryCSS(size);
  const _linearProgressCSS = linearProgressCSS(size);
  const _onClose = () => {
    setIsClose(true);
    onClose === null || onClose === void 0 ? void 0 : onClose();
  };
  useEffect(() => {
    setTimeout(() => {
      setPercentState(0);
    }, 100);
  }, []);
  useEffect(() => {
    if (ref.current && isClose) ref.current.style.opacity = 0;
  }, [isClose]);
  useEffect(() => {
    setTimeout(() => {
      if (autoDisappear) _onClose();
    }, duration);
  }, [autoDisappear]);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const AlertView = useMemo(() => {
    let node = children;
    if (!node) {
      node = jsx(Icon, {
        name: iconName,
        width: iconSize,
        height: iconSize,
        color: 'currentColor',
        viewBox: true
      });
    }
    return jsx("div", {
      css: _AlertInfoCSS,
      className: classNames('DGN-UI-Alert', className)
    }, jsx("span", {
      css: AlertIconCSS,
      className: 'DGN-UI-Alert-Icon'
    }, node), jsx("span", {
      css: _AlertTextCSS,
      className: 'DGN-UI-Alert-Content'
    }, jsx("span", {
      css: _AlertPrimaryCSS,
      className: 'DGN-UI-Alert-Content-Primary'
    }, children ? typeof children === 'string' ? children : jsx("children", null) : text), secondary && jsx("span", {
      css: _AlertSecondaryCSS,
      className: 'DGN-UI-Alert-Content-Secondary'
    }, secondary)), jsx("span", {
      css: AlertClearIconCSS,
      onClick: _onClose,
      ref: clearIconRef,
      className: 'DGN-UI-Alert-Clear'
    }, clearAble ? jsx(ButtonIcon, {
      circular: true,
      viewType: 'text',
      size: size,
      color: type,
      name: 'Close'
    }) : null));
  }, [color, text, clearAble, secondary, size, children, className, type, onClose, mainColor, opacityColor, isClose]);
  const LinearView = useMemo(() => {
    return progressing ? jsx("span", {
      css: _linearProgressCSS,
      className: 'DGN-UI-Progress'
    }, jsx(LinearProgress, {
      determinate: true,
      background: 'transparent',
      color: mainColor,
      percent: percentState,
      duration: duration / 1000,
      height: linearProgressSizeMap.get(size),
      style: {
        boxShadow: 'none'
      }
    })) : null;
  }, [progressing, size, duration, percentState]);
  return showAlert ? jsx("div", {
    css: _AlertContainerCSS,
    className: 'DGN-UI-Container-Alert',
    ref: ref
  }, AlertView, LinearView) : null;
}));
const fadeIn = animations.fadeIn;
const AlertContainerCSS = size => css`
    ${positionRelative};
    ${displayBlock};
    max-width: ${maxWidthMap.get(size)}px;
    transition: opacity ${transitionTime}ms ease;
    margin-bottom: 1rem;
    pointer-events: auto;
    z-index: ${zIndexCORE(1)};
    animation: ${fadeIn} ${transitionTime}ms ease;
`;
const AlertInfoCSS = (mainColor, opacityColor) => css`
    ${flexRow};
    ${positionRelative};
    ${boxBorder};
    ${borderRadius4px};
    ${breakWord};
    ${border(1, mainColor)};
    color: ${mainColor};
    background-image: linear-gradient(${opacityColor}, ${opacityColor});
    background-color: ${systemWhite};
    width: 100%;
    min-width: 320px;
    padding: ${spacing([0, 1, 0, 4])};
    opacity: 1;
`;
const AlertIconCSS = css`
    display: inherit;
    margin-right: ${spacing([2.5])};
    padding: ${spacing([2, 0])};
`;
const AlertTextCSS = clearAble => css`
    ${displayBlock};
    margin-right: ${spacing([clearAble ? 2.5 : 0])};
    padding: ${spacing([2, 0])};
    align-self: center;
`;
const AlertPrimaryCSS = (secondary, size) => css`
    ${flexRow};
    ${positionRelative};
    ${itemsCenter};
    ${justifyStart};
    ${userSelectNone};
    ${secondary ? primaryTypoMap.get(size) : paragraph1};
`;
const AlertSecondaryCSS = size => css`
    ${displayBlock};
    ${userSelectNone};
    ${size === 'tiny' ? paragraph3 : paragraph2};
`;
const AlertClearIconCSS = css`
    margin-left: auto;
`;
const linearProgressCSS = size => css`
    ${displayBlock};
    ${positionRelative};
    margin-top: ${spacing([size === 'medium' ? 1 : 0.5])};
    padding: ${spacing([0, 1.25])};
`;
Alert.defaultProps = {
  autoDisappear: false,
  className: '',
  clearAble: true,
  color: 'info',
  duration: 2000,
  progressing: false,
  size: 'medium'
};
Alert.propTypes = {
  /** Automatically remove the Alert component after the timer ends (milliseconds). */
  autoDisappear: PropTypes.bool,
  /** The element to display in alert like text props (priority). */
  children: PropTypes.node,
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, show clear icon to close alert. */
  clearAble: PropTypes.bool,
  /** The amount of time the alert is displayed. The unit is the milliseconds. */
  duration: PropTypes.number,
  /** Callback fired when the component closed. */
  onClose: PropTypes.func,
  /** If `true`, show linear progress in duration time.*/
  progressing: PropTypes.bool,
  /** The secondary text to show in alert (under primary). */
  secondary: PropTypes.string,
  /** The size of the component. */
  size: PropTypes.oneOf(['tiny', 'small', 'medium']),
  /** The text to show in alert. */
  text: PropTypes.string,
  /** Type of the component. */
  type: PropTypes.oneOf(['info', 'success', 'warning', 'danger'])
};
export default Alert;